import { useMemo } from 'react';
import { Pause, Play, Radio } from 'lucide-react';
import { Button, IconButton } from '../../ui';
import { useTranslation } from '../../../../lib/i18n';
import { useLocale } from '../../../../lib/locale-context';
import { REPLAY_WINDOW_MS } from '../../../../lib/home-map/map-replay';
import type { MapEvent } from '../../../../lib/home-map/map-events';
import type { MapMode } from '../../../../lib/home-map/use-map-live';
import styles from './MapTimeline.module.css';

/**
 * The map's clock: the last 24 hours as a track with one tick per event, a playhead, play / pause, and a
 * LIVE indicator once the reel has caught up. Dragging the track scrubs — the map shows what had happened
 * by that moment (parked particles) — and Play from there replays to now.
 *
 * The scrubber is a native range input under a drawn track: keyboard- and screen-reader-operable for free,
 * with the ticks and the playhead painted around it.
 */
export function MapTimeline({
  history,
  mode,
  playhead,
  onPlay,
  onPause,
  onScrub,
  onLive,
  onPick,
}: {
  history: MapEvent[];
  mode: MapMode;
  playhead: number | null;
  onPlay: (fromAt?: number) => void;
  onPause: () => void;
  onScrub: (at: number) => void;
  onLive: () => void;
  /** A tick was clicked: scrub to that moment and open that event's card. */
  onPick?: (event: MapEvent) => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const now = Date.now();
  const from = now - REPLAY_WINDOW_MS;
  const value = playhead ?? now;
  const fmt = useMemo(() => new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }), [locale]);
  // Each tick says what it is on hover — the time and the event's own line.
  const ticks = useMemo(
    () =>
      history
        .filter((e) => e.at >= from && e.at <= now)
        .map((e) => ({
          id: e.id,
          event: e,
          pct: ((e.at - from) / REPLAY_WINDOW_MS) * 100,
          kind: e.kind,
          title: `${fmt.format(new Date(e.at))} · ${e.text || e.kind}`,
        })),
    [history, from, now, fmt],
  );
  const pct = Math.min(100, Math.max(0, ((value - from) / REPLAY_WINDOW_MS) * 100));
  const timeLabel = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(value));

  return (
    <div className={styles.root} data-mode={mode}>
      {mode === 'playing' ? (
        <IconButton size="small" variant="outlined" aria-label={t('home.map.pause')} onClick={onPause}>
          <Pause size={16} aria-hidden />
        </IconButton>
      ) : (
        <IconButton
          size="small"
          variant="outlined"
          aria-label={t('home.map.play')}
          onClick={() => onPlay(mode === 'scrubbing' && playhead ? playhead : undefined)}
        >
          <Play size={16} aria-hidden />
        </IconButton>
      )}
      <div className={styles.trackWrap}>
        <div className={styles.track} aria-hidden>
          {ticks.map((tick) => (
            <button
              key={tick.id}
              type="button"
              className={styles.tick}
              data-kind={tick.kind}
              style={{ insetInlineStart: `${tick.pct}%` }}
              aria-label={tick.title}
              onClick={() => onPick?.(tick.event)}
            />
          ))}
          <span className={styles.played} style={{ width: `${pct}%` }} />
          <span className={styles.head} style={{ insetInlineStart: `${pct}%` }} />
          {/* The clock: the moment the playhead is at, riding above it whenever the map is in the past. */}
          {mode !== 'live' && (
            <span className={styles.headTime} style={{ insetInlineStart: `${pct}%` }} data-playing={mode === 'playing'}>
              {timeLabel}
            </span>
          )}
        </div>
        <input
          id="home-map-timeline"
          className={styles.range}
          type="range"
          min={from}
          max={now}
          step={60_000}
          value={value}
          aria-label={t('home.map.timeline')}
          aria-valuetext={timeLabel}
          onChange={(e) => onScrub(Number(e.target.value))}
        />
      </div>
      <Button
        size="tiny"
        variant={mode === 'live' ? 'contained' : 'outlined'}
        className={styles.live}
        onClick={onLive}
        aria-pressed={mode === 'live'}
        startIcon={<Radio size={12} aria-hidden />}
      >
        {mode === 'live' ? t('home.map.live') : mode === 'playing' ? t('home.map.playing', { time: timeLabel }) : timeLabel}
      </Button>
    </div>
  );
}
